// D22 notif-consumer — in-app toast surface for `notification.*` realtime
// events (Q10 stop-gap until Wave 8 BSPs ship Slack/Telegram/email
// channels).
//
// Architecture:
//   - Mounted under the authed shell (`AppShell`) so unauthenticated
//     surfaces never see the listener and we don't leak across logout.
//   - Subscribes to two kinds via `useUserHubChannel`:
//       * notification.hotLead       → toast `info`, CTA → /leads
//       * notification.reviewPending → toast `info`, CTA → /inbox?selectedId=<id>
//   - Dedupes by entity id (`leadId` / `draftId`) — the toast bus's `id`
//     contract lets us update-in-place if the same event fires twice (cron
//     re-emit, reconnect replay).
//   - Auto-dismiss leans on the DS Toaster default duration (5s); we pass
//     `duration: 8000` per Q10 spec.
//   - Renders nothing — the toast bus is module-scoped, so the listener is
//     a side-effect-only component.

import { useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { useT } from '@barel/i18n';
import { useUserHubChannel } from '@barel/api-client/react';
import { toast } from '@barel/ui';

import {
  NOTIFICATION_HOT_LEAD_KIND,
  NOTIFICATION_REVIEW_PENDING_KIND,
  isHotLeadPayload,
  isReviewPendingPayload,
  type NotificationFrame,
  type NotificationHotLeadPayload,
  type NotificationReviewPendingPayload,
} from './types';

/** Auto-dismiss after 8s per the D22 spec — long enough to read a 2-line
 * banner, short enough to not pile up. The DS Toaster keeps the toast open
 * while the pointer hovers (Radix default). */
const AUTO_DISMISS_MS = 8000;

/** Stable toast ids — same entity = same id, so a re-emit updates in place
 * instead of stacking. The prefix keeps notification toasts disjoint from
 * other feature toasts (which mint random ids). */
function hotLeadToastId(leadId: string): string {
  return `notif:hotLead:${leadId}`;
}
function reviewPendingToastId(draftId: string): string {
  return `notif:reviewPending:${draftId}`;
}

/** `<NotificationListener />` — mount once under the authenticated app
 * shell. Returns `null`; toasts render through the module-scoped DS
 * `Toaster` bus already mounted in `<App>`. */
export function NotificationListener() {
  const t = useT('notifications');
  const navigate = useNavigate();

  const handleHotLead = useCallback(
    (frame: NotificationFrame<NotificationHotLeadPayload>) => {
      if (frame.type !== NOTIFICATION_HOT_LEAD_KIND) return;
      if (!isHotLeadPayload(frame.payload)) return;
      const { leadId, title, score } = frame.payload;
      const body = title
        ? t('hotLead.body', { contactName: title, score })
        : t('hotLead.bodyFallback', { score });
      toast.info({
        id: hotLeadToastId(leadId),
        title: t('hotLead.title'),
        description: body,
        duration: AUTO_DISMISS_MS,
        action: {
          label: t('hotLead.cta'),
          // Leads list is the only route surface today — the lead-detail
          // overlay opens via in-page state, not a route. Wave 11 may add
          // `/leads/$leadId` and we'll thread the id then.
          onClick: () => {
            void navigate({ to: '/leads' as never });
          },
        },
      });
    },
    [t, navigate],
  );

  const handleReviewPending = useCallback(
    (frame: NotificationFrame<NotificationReviewPendingPayload>) => {
      if (frame.type !== NOTIFICATION_REVIEW_PENDING_KIND) return;
      if (!isReviewPendingPayload(frame.payload)) return;
      const { draftId, conversationId, contactName } = frame.payload;
      const body = contactName
        ? t('reviewPending.body', { contactName })
        : t('reviewPending.bodyFallback');
      toast.info({
        id: reviewPendingToastId(draftId),
        title: t('reviewPending.title'),
        description: body,
        duration: AUTO_DISMISS_MS,
        action: {
          label: t('reviewPending.cta'),
          onClick: () => {
            // Inbox reads `selectedId` from its search params (see
            // `app/_authed/inbox/__layout.tsx`) — passing it pre-selects
            // the thread the draft is queued against.
            void navigate({
              to: '/inbox' as never,
              search: { selectedId: conversationId } as never,
            });
          },
        },
      });
    },
    [t, navigate],
  );

  useUserHubChannel(NOTIFICATION_HOT_LEAD_KIND, handleHotLead);
  useUserHubChannel(NOTIFICATION_REVIEW_PENDING_KIND, handleReviewPending);

  return null;
}
